import type { TooltipProps } from 'recharts'
import type { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent'

/**
 * WCAG-compliant replacement for Recharts' DefaultTooltipContent, which colors each
 * row's text with its series color — light series (pollen, powder blue) fail 4.5:1 on
 * the popover. Here all text wears the popover-foreground token and series identity is
 * carried by a small color swatch instead. Pass as:
 *
 *   <Tooltip content={<ChartTooltipContent />} formatter={...} />
 *
 * Recharts clones the content element with all Tooltip props (formatter, labelFormatter,
 * separator, payload, label), so existing per-chart formatters keep working unchanged.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  separator = ' : ',
}: TooltipProps<ValueType, NameType>) {
  if (!active || !payload || payload.length === 0) return null

  // Mirrors DefaultTooltipContent: entries from series with tooltipType="none" are skipped.
  const entries = payload.filter((entry) => entry.type !== 'none')
  if (entries.length === 0) return null

  const finalLabel = labelFormatter && label != null ? labelFormatter(label, payload) : label

  return (
    <div
      role="status"
      className="rounded-lg border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md"
    >
      {finalLabel != null && finalLabel !== '' && <p className="mb-2 font-bold">{finalLabel}</p>}
      <ul className="m-0 list-none space-y-1 p-0">
        {entries.map((entry, index) => {
          let value: React.ReactNode = entry.value as React.ReactNode
          let name: React.ReactNode = entry.name as React.ReactNode
          if (formatter && entry.value !== undefined && entry.name !== undefined) {
            const formatted = formatter(
              entry.value,
              entry.name,
              entry as Payload<ValueType, NameType>,
              index,
              payload
            )
            if (Array.isArray(formatted)) {
              ;[value, name] = formatted as [React.ReactNode, React.ReactNode]
            } else if (formatted != null) {
              value = formatted as React.ReactNode
            }
          }
          const swatchColor = entry.color ?? (entry.payload as { fill?: string })?.fill
          return (
            <li key={`item-${index}`} className="flex items-center gap-2">
              {swatchColor && (
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: swatchColor }}
                />
              )}
              <span>
                {name != null && name !== '' ? (
                  <>
                    {name}
                    {separator}
                  </>
                ) : null}
                {value}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
